import React, { useState, useMemo } from 'react'
import { supabase } from '../supabaseClient'
import { isMeetLinkVisible } from '../meetHelper'

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtTime(t) {
  if (!t) return ''
  return new Date('2000-01-01T' + t).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true
  })
}
function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
  })
}
function fmtDateShort(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short'
  })
}

// FIX: Use local date instead of UTC to avoid timezone offset issues
// Previously: new Date().toISOString().slice(0,10) — this is UTC, wrong for IST/GST
// Now: uses local year/month/day which is correct for any timezone
function getLocalDateStr(offsetDays = 0) {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getDayLabel(d) {
  const today    = getLocalDateStr(0)
  const tomorrow = getLocalDateStr(1)
  if (d === today)    return { label:'Today',    color:'#f87171' }
  if (d === tomorrow) return { label:'Tomorrow', color:'#f4a335' }
  return null
}

function daysUntil(d) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(d + 'T00:00:00')
  return Math.ceil((target - today) / 86400000)
}

function groupByDate(classes) {
  const groups = {}
  classes.forEach(c => {
    if (!groups[c.class_date]) groups[c.class_date] = []
    groups[c.class_date].push(c)
  })
  return Object.entries(groups).sort(([a],[b]) => a.localeCompare(b))
}
function groupByMonth(classes) {
  const groups = {}
  classes.forEach(c => {
    const month = c.class_date.slice(0,7)
    if (!groups[month]) groups[month] = []
    groups[month].push(c)
  })
  return Object.entries(groups).sort(([a],[b]) => a.localeCompare(b))
}
function monthLabel(ym) {
  const [y,m] = ym.split('-')
  return new Date(y, m-1).toLocaleString('en-IN', { month:'long', year:'numeric' })
}

// ── Single class row ─────────────────────────────────────────────────────────
function ClassRow({ c, onEditZoom, onDelete, showTeacher, compact, expandable, onJoin, gateLink }) {
  const today   = getLocalDateStr(0)
  const isPast  = c.class_date < today
  const isToday = c.class_date === today
  const days    = daysUntil(c.class_date)

  const [open,         setOpen]         = useState(false)
  const [roster,       setRoster]       = useState(null)
  const [rosterLoading,setRosterLoading]= useState(false)

  const statusColor = isPast
    ? 'rgba(var(--fg-rgb),0.08)'
    : isToday
    ? 'rgba(239,68,68,0.12)'
    : 'rgba(30,144,255,0.08)'

  const borderColor = isPast
    ? 'rgba(var(--fg-rgb),0.06)'
    : isToday
    ? 'rgba(239,68,68,0.35)'
    : 'rgba(30,144,255,0.2)'

  async function toggleOpen() {
    if (!expandable) return
    if (!open && roster === null) {
      setRosterLoading(true)
      const { data: enr } = await supabase.from('enrollments').select('id,student_id,course_id').eq('class_id', c.id)
      const studentIds = [...new Set((enr||[]).map(e => e.student_id).filter(Boolean))]
      const courseIds  = [...new Set((enr||[]).map(e => e.course_id).filter(Boolean))]
      const [{ data: students }, { data: courses }] = await Promise.all([
        studentIds.length ? supabase.from('profiles').select('id,full_name,email,phone').in('id', studentIds) : Promise.resolve({ data: [] }),
        courseIds.length  ? supabase.from('courses').select('id,title').in('id', courseIds)                   : Promise.resolve({ data: [] }),
      ])
      const merged = (enr||[])
        .map(e => ({ ...e, student: students?.find(s => s.id === e.student_id), course: courses?.find(co => co.id === e.course_id) }))
        .filter(r => r.student)
      setRoster(merged)
      setRosterLoading(false)
    }
    setOpen(o => !o)
  }

  return (
    <div style={{ borderRadius:'10px', overflow:'hidden' }}>
      <div onClick={toggleOpen} style={{
        display:'flex', alignItems:'center', gap:'10px',
        padding: compact ? '8px 12px' : '11px 14px',
        background: statusColor,
        border: `0.5px solid ${borderColor}`,
        borderRadius: open ? '10px 10px 0 0' : '10px',
        opacity: isPast ? 0.65 : 1,
        transition:'all 0.15s',
        cursor: expandable ? 'pointer' : 'default',
      }}>
      <div style={{ width:'52px', flexShrink:0, textAlign:'center' }}>
        {c.start_time ? (
          <div style={{ fontSize:'12px', fontWeight:700, color: isPast ? 'rgba(var(--fg-rgb),var(--a40))' : isToday ? '#f87171' : '#5aabff' }}>
            {fmtTime(c.start_time)}
          </div>
        ) : (
          <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a20))' }}>No time</div>
        )}
      </div>

      <div style={{ width:'1px', height:'32px', background:'rgba(var(--fg-rgb),0.08)', flexShrink:0 }}/>

      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:'13px', fontWeight:600, color: isPast ? 'rgba(var(--fg-rgb),var(--a50))' : 'rgba(var(--fg-rgb),var(--a90))', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {c.is_emergency && <span style={{ fontSize:'9px', color:'#f87171', fontWeight:800, marginRight:'5px', background:'rgba(239,68,68,0.15)', padding:'1px 6px', borderRadius:'4px' }}>EMERGENCY</span>}
          {c.title}
        </div>
        <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'2px', display:'flex', gap:'8px', flexWrap:'wrap' }}>
          {c.batch       && <span>📚 {c.batch}</span>}
          {showTeacher && c.teacher_name && <span>👨‍🏫 {c.teacher_name}</span>}
          {c.student_names && <span>🎓 {c.student_names}</span>}
          {c.class_timezone && <span>🌐 {c.class_timezone}</span>}
        </div>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:'6px', flexShrink:0 }}>
        {isPast ? (
          <span style={{ fontSize:'9px', fontWeight:700, padding:'3px 8px', borderRadius:'6px', background:'rgba(16,185,129,0.1)', color:'#34d399' }}>✓ Done</span>
        ) : isToday ? (
          <span style={{ fontSize:'9px', fontWeight:700, padding:'3px 8px', borderRadius:'6px', background:'rgba(239,68,68,0.15)', color:'#f87171', animation:'pulse 1.5s infinite' }}>● TODAY</span>
        ) : days === 1 ? (
          <span style={{ fontSize:'9px', fontWeight:700, padding:'3px 8px', borderRadius:'6px', background:'rgba(244,163,53,0.15)', color:'#f4a335' }}>Tomorrow</span>
        ) : days <= 7 ? (
          <span style={{ fontSize:'9px', fontWeight:700, padding:'3px 8px', borderRadius:'6px', background:'rgba(96,165,250,0.15)', color:'#60a5fa' }}>In {days}d</span>
        ) : null}

        {c.meet_link && (!gateLink || isMeetLinkVisible(c.class_date, c.start_time)) ? (
          <a href={c.meet_link} target="_blank" rel="noreferrer" onClick={e => { e.stopPropagation(); onJoin?.(c) }}
            style={{ fontSize:'10px', fontWeight:700, padding:'4px 10px', borderRadius:'7px', background:'rgba(30,144,255,0.15)', color:'#5aabff', border:'0.5px solid rgba(30,144,255,0.25)', textDecoration:'none', whiteSpace:'nowrap' }}>
            🔗 Join
          </a>
        ) : c.meet_link && !isPast ? (
          <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a25))', fontStyle:'italic' }}>🔒 Visible 24h before</span>
        ) : !isPast && (
          <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a20))', fontStyle:'italic' }}>No link</span>
        )}

        {onEditZoom && !isPast && (
          <button onClick={e => { e.stopPropagation(); onEditZoom(c) }}
            style={{ fontSize:'10px', padding:'4px 8px', borderRadius:'7px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a40))', border:'0.5px solid rgba(var(--fg-rgb),0.1)', cursor:'pointer', whiteSpace:'nowrap' }}>
            ✏ Zoom
          </button>
        )}
        {onDelete && (
          <button onClick={e => { e.stopPropagation(); onDelete(c.id, c.title) }}
            style={{ fontSize:'10px', padding:'4px 8px', borderRadius:'7px', background:'rgba(239,68,68,0.12)', color:'#f87171', border:'0.5px solid rgba(239,68,68,0.2)', cursor:'pointer', whiteSpace:'nowrap' }}>
            🗑
          </button>
        )}
        {expandable && (
          <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a25))', marginLeft:'2px' }}>{open ? '▲' : '▼'}</span>
        )}
      </div>
      </div>

      {expandable && open && (
        <div style={{ padding:'10px 14px 12px', background:'rgba(var(--fg-rgb),0.02)', border:`0.5px solid ${borderColor}`, borderTop:'none', borderRadius:'0 0 10px 10px' }}>
          <div style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a35))', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:'8px' }}>
            Class Details
          </div>
          <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', display:'grid', gap:'3px', marginBottom:'10px' }}>
            <div>📅 {fmtDate(c.class_date)} {c.start_time && '· ' + fmtTime(c.start_time)}</div>
            {c.teacher_name && <div>👨‍🏫 Teacher: {c.teacher_name}</div>}
            {c.batch && <div>📚 Batch: {c.batch}</div>}
            {c.created_by && <div>🗂 Created by: {c.created_by}</div>}
          </div>
          <div style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a35))', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:'6px' }}>
            Enrolled Students {roster && `(${roster.length})`}
          </div>
          {rosterLoading ? (
            <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a25))' }}>Loading roster...</div>
          ) : !roster || roster.length === 0 ? (
            <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a25))' }}>No students enrolled in this class yet.</div>
          ) : (
            <div style={{ display:'grid', gap:'4px' }}>
              {roster.map(r => (
                <div key={r.id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'6px 10px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'8px', flexWrap:'wrap' }}>
                  <span style={{ fontSize:'12px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a75))' }}>{r.student.full_name}</span>
                  {r.student.email && <a href={`mailto:${r.student.email}`} style={{ fontSize:'11px', color:'#5aabff', textDecoration:'none' }}>{r.student.email}</a>}
                  {r.student.phone && <span style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))' }}>{r.student.phone}</span>}
                  {r.course && <span style={{ fontSize:'10px', color:'#a78bfa', marginLeft:'auto' }}>{r.course.title}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Date group header ─────────────────────────────────────────────────────────
function DateHeader({ date }) {
  const today    = getLocalDateStr(0)
  const isToday  = date === today
  const isFuture = date > today
  const dl       = getDayLabel(date)

  return (
    <div style={{ display:'flex', alignItems:'center', gap:'10px', margin:'16px 0 8px' }}>
      <div style={{
        fontSize:'11px', fontWeight:800, letterSpacing:'0.06em', textTransform:'uppercase',
        color: isToday ? '#f87171' : isFuture ? '#5aabff' : 'rgba(var(--fg-rgb),var(--a30))',
      }}>
        {fmtDate(date)}
        {dl && <span style={{ marginLeft:'8px', color:dl.color, fontSize:'10px', background:`${dl.color}18`, padding:'2px 8px', borderRadius:'6px' }}>{dl.label}</span>}
      </div>
      <div style={{ flex:1, height:'1px', background: isToday ? 'rgba(239,68,68,0.2)' : 'rgba(var(--fg-rgb),0.05)' }}/>
    </div>
  )
}

// ── Month header ──────────────────────────────────────────────────────────────
function MonthHeader({ ym, count }) {
  const currentMonth = getLocalDateStr(0).slice(0,7)
  const isNow = ym === currentMonth
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'10px', margin:'24px 0 4px' }}>
      <div style={{ fontSize:'13px', fontWeight:800, color: isNow ? '#5aabff' : 'rgba(var(--fg-rgb),var(--a50))', display:'flex', alignItems:'center', gap:'8px' }}>
        {isNow && <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:'#5aabff', display:'inline-block' }}/>}
        {monthLabel(ym)}
        <span style={{ fontSize:'11px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a25))', background:'rgba(var(--fg-rgb),0.05)', padding:'2px 8px', borderRadius:'10px' }}>{count} class{count!==1?'es':''}</span>
      </div>
      <div style={{ flex:1, height:'1px', background:'rgba(var(--fg-rgb),0.06)' }}/>
    </div>
  )
}

// ── Mini calendar view ────────────────────────────────────────────────────────
function MiniCalendarView({ classes, onEditZoom, onDelete, showTeacher, expandable, onJoin, gateLink }) {
  const today = new Date()
  const [viewYear,  setViewYear]  = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())

  const classDates = useMemo(() => {
    const map = {}
    classes.forEach(c => {
      if (!map[c.class_date]) map[c.class_date] = []
      map[c.class_date].push(c)
    })
    return map
  }, [classes])

  const firstDay    = new Date(viewYear, viewMonth, 1).getDay()
  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate()
  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  const todayStr = getLocalDateStr(0)
  const [selected, setSelected] = useState(todayStr)

  function pad(n) { return String(n).padStart(2,'0') }
  function dateStr(d) { return `${viewYear}-${pad(viewMonth+1)}-${pad(d)}` }

  const selectedClasses = selected ? (classDates[selected] || []) : []

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
          <button onClick={() => { if(viewMonth===0){setViewMonth(11);setViewYear(y=>y-1)}else setViewMonth(m=>m-1) }}
            style={{ background:'rgba(var(--fg-rgb),0.06)', border:'none', color:'rgba(var(--fg-rgb),var(--a50))', borderRadius:'8px', width:'30px', height:'30px', cursor:'pointer', fontSize:'14px' }}>‹</button>
          <div style={{ fontSize:'14px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))' }}>
            {new Date(viewYear, viewMonth).toLocaleString('en-IN', { month:'long', year:'numeric' })}
          </div>
          <button onClick={() => { if(viewMonth===11){setViewMonth(0);setViewYear(y=>y+1)}else setViewMonth(m=>m+1) }}
            style={{ background:'rgba(var(--fg-rgb),0.06)', border:'none', color:'rgba(var(--fg-rgb),var(--a50))', borderRadius:'8px', width:'30px', height:'30px', cursor:'pointer', fontSize:'14px' }}>›</button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'2px', marginBottom:'4px' }}>
          {['S','M','T','W','T','F','S'].map((d,i) => (
            <div key={i} style={{ textAlign:'center', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a25))', padding:'4px 0' }}>{d}</div>
          ))}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'2px' }}>
          {cells.map((d, i) => {
            if (!d) return <div key={i}/>
            const ds       = dateStr(d)
            const hasClass = classDates[ds]?.length > 0
            const isToday  = ds === todayStr
            const isSel    = ds === selected
            const isPast   = ds < todayStr
            return (
              <button key={i} onClick={() => setSelected(ds)}
                style={{
                  padding:'6px 2px', borderRadius:'8px', border:'none', cursor:'pointer',
                  background: isSel ? '#1e90ff' : isToday ? 'rgba(239,68,68,0.15)' : hasClass ? 'rgba(30,144,255,0.1)' : 'transparent',
                  color: isSel ? '#fff' : isToday ? '#f87171' : isPast ? 'rgba(var(--fg-rgb),var(--a20))' : hasClass ? '#5aabff' : 'rgba(var(--fg-rgb),var(--a50))',
                  fontSize:'12px', fontWeight: hasClass || isToday ? 700 : 400,
                  position:'relative',
                }}>
                {d}
                {hasClass && !isSel && (
                  <div style={{ position:'absolute', bottom:'2px', left:'50%', transform:'translateX(-50%)', width:'4px', height:'4px', borderRadius:'50%', background: isPast ? '#34d399' : '#1e90ff' }}/>
                )}
              </button>
            )
          })}
        </div>

        <div style={{ display:'flex', gap:'12px', marginTop:'12px', fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}>
          <span><span style={{ color:'#1e90ff' }}>●</span> Has class</span>
          <span><span style={{ color:'#f87171' }}>●</span> Today</span>
          <span><span style={{ color:'#34d399' }}>●</span> Completed</span>
        </div>
      </div>

      <div>
        <div style={{ fontSize:'12px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a50))', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.06em' }}>
          {fmtDate(selected)}
          {getDayLabel(selected) && <span style={{ marginLeft:'8px', color:getDayLabel(selected).color }}>{getDayLabel(selected).label}</span>}
        </div>
        {selectedClasses.length === 0 ? (
          <div style={{ textAlign:'center', padding:'2rem 1rem', background:'rgba(var(--fg-rgb),0.02)', borderRadius:'10px', border:'0.5px solid rgba(var(--fg-rgb),0.05)' }}>
            <div style={{ fontSize:'28px', marginBottom:'8px' }}>📭</div>
            <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a25))' }}>No classes on this day</div>
          </div>
        ) : (
          <div style={{ display:'grid', gap:'8px' }}>
            {selectedClasses
              .sort((a,b) => (a.start_time||'').localeCompare(b.start_time||''))
              .map(c => <ClassRow key={c.id} c={c} onEditZoom={onEditZoom} onDelete={onDelete} showTeacher={showTeacher} expandable={expandable} onJoin={onJoin} gateLink={gateLink} compact/>)}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main exported component ───────────────────────────────────────────────────
export default function ClassScheduleView({ classes = [], onEditZoom, onDelete, showTeacher = false, role = 'student', onJoin, defaultView = 'upcoming' }) {
  const [view,    setView]    = useState(defaultView)
  const [search,  setSearch]  = useState('')
  const [showAll, setShowAll] = useState(false)
  const [allFilter, setAllFilter] = useState('all') // all | previous | upcoming | today — teacher's "All" sub-filter

  const isStudent = role === 'student'
  const isTeacher = role === 'teacher'
  const expandable = role === 'admin'
  const PREVIEW   = 5

  const today    = getLocalDateStr(0)
  const upcoming = classes.filter(c => c.class_date >= today)
  const past     = classes.filter(c => c.class_date <  today)

  const filtered = useMemo(() => {
    let list = view === 'past' ? past : view === 'all' ? [...classes] : upcoming
    if (search) list = list.filter(c =>
      c.title?.toLowerCase().includes(search.toLowerCase()) ||
      c.teacher_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.batch?.toLowerCase().includes(search.toLowerCase()) ||
      c.student_names?.toLowerCase().includes(search.toLowerCase())
    )
    return list.sort((a,b) => {
      const dd = a.class_date.localeCompare(b.class_date)
      if (dd !== 0) return dd
      return (a.start_time||'').localeCompare(b.start_time||'')
    })
  }, [classes, view, search])

  const displayList = (isStudent && view === 'upcoming' && !showAll)
    ? filtered.slice(0, PREVIEW)
    : (isTeacher && view === 'all' && allFilter !== 'all')
      ? filtered.filter(c =>
          allFilter === 'previous' ? c.class_date < today :
          allFilter === 'upcoming' ? c.class_date > today :
          c.class_date === today // 'today'
        )
      : filtered

  const grouped = groupByDate(displayList)
  const byMonth = groupByMonth(displayList)

  const inp = {
    background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)',
    borderRadius:'10px', padding:'8px 12px', fontSize:'13px',
    color:'rgba(var(--fg-rgb),var(--a80))', outline:'none', fontFamily:'inherit',
    colorScheme:'dark', flex:1,
  }
  const tabBtn = (id, label, count) => (
    <button onClick={() => { setView(id); setShowAll(false) }} style={{
      fontSize:'12px', fontWeight:700, padding:'7px 14px', borderRadius:'10px', border:'none',
      cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap',
      background: view===id ? '#1e90ff' : 'rgba(var(--fg-rgb),0.05)',
      color:      view===id ? '#fff'    : 'rgba(var(--fg-rgb),var(--a45))',
    }}>
      {label} {count !== undefined && <span style={{ opacity:0.7 }}>({count})</span>}
    </button>
  )
  const filterPill = (id, label) => (
    <button onClick={() => setAllFilter(id)} style={{
      fontSize:'11px', fontWeight:700, padding:'5px 12px', borderRadius:'20px', border:'none',
      cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap',
      background: allFilter===id ? '#1e90ff' : 'rgba(var(--fg-rgb),0.05)',
      color:      allFilter===id ? '#fff'    : 'rgba(var(--fg-rgb),var(--a45))',
    }}>
      {label}
    </button>
  )

  return (
    <div>
      <div style={{ display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
          {!isTeacher && tabBtn('upcoming', '📅 Upcoming', upcoming.length)}
          {tabBtn('calendar', '🗓 Calendar')}
          {!isStudent && tabBtn('all', '📋 All', classes.length)}
          {!isStudent && !isTeacher && tabBtn('past', '✅ Past', past.length)}
        </div>
        {view !== 'calendar' && (
          <input style={inp} type="text" placeholder="🔍 Search by class, teacher, or student..."
            value={search} onChange={e => setSearch(e.target.value)}/>
        )}
      </div>

      {isTeacher && view === 'all' && (
        <div style={{ display:'flex', gap:'6px', marginBottom:'16px', flexWrap:'wrap' }}>
          {filterPill('all',      'All')}
          {filterPill('previous', '⏮ Previous')}
          {filterPill('today',    '🔴 Current')}
          {filterPill('upcoming', '⏭ Upcoming')}
        </div>
      )}

      {!isStudent && (
        <div style={{ display:'flex', gap:'10px', marginBottom:'16px', flexWrap:'wrap' }}>
          {[
            { label:'Total',     value:classes.length,                                       color:'rgba(var(--fg-rgb),var(--a50))' },
            { label:'Upcoming',  value:upcoming.length,                                      color:'#5aabff'               },
            { label:'Today',     value:classes.filter(c=>c.class_date===today).length,       color:'#f87171'               },
            { label:'With Link', value:classes.filter(c=>c.meet_link).length,                color:'#10b981'               },
            { label:'Past',      value:past.length,                                          color:'rgba(var(--fg-rgb),var(--a30))' },
          ].map(s => (
            <div key={s.label} style={{ padding:'6px 14px', background:'rgba(var(--fg-rgb),0.04)', borderRadius:'20px', border:'0.5px solid rgba(var(--fg-rgb),0.07)', fontSize:'12px', fontWeight:700, color:s.color, display:'flex', gap:'6px', alignItems:'center' }}>
              <span style={{ color:'rgba(var(--fg-rgb),var(--a30))', fontWeight:400 }}>{s.label}</span> {s.value}
            </div>
          ))}
        </div>
      )}

      {view === 'calendar' && (
        <MiniCalendarView classes={classes} onEditZoom={onEditZoom} onDelete={onDelete} showTeacher={showTeacher} expandable={expandable} onJoin={onJoin} gateLink={isTeacher || isStudent}/>
      )}

      {view !== 'calendar' && (
        <>
          {filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem 1rem', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'14px', border:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
              <div style={{ fontSize:'36px', marginBottom:'12px' }}>{view==='past'?'📼':'📅'}</div>
              <div style={{ fontSize:'14px', color:'rgba(var(--fg-rgb),var(--a40))' }}>
                {search ? 'No classes match your search.' : view==='past' ? 'No completed classes yet.' : 'No upcoming classes.'}
              </div>
            </div>
          ) : view === 'all' ? (
            byMonth.map(([ym, mClasses]) => (
              <div key={ym}>
                <MonthHeader ym={ym} count={mClasses.length}/>
                <div style={{ display:'grid', gap:'6px' }}>
                  {mClasses
                    .sort((a,b) => a.class_date.localeCompare(b.class_date) || (a.start_time||'').localeCompare(b.start_time||''))
                    .map(c => <ClassRow key={c.id} c={c} onEditZoom={onEditZoom} onDelete={onDelete} showTeacher={showTeacher} expandable={expandable} onJoin={onJoin} gateLink={isTeacher || isStudent}/>)}
                </div>
              </div>
            ))
          ) : (
            <>
              {grouped.map(([date, dClasses]) => (
                <div key={date}>
                  <DateHeader date={date}/>
                  <div style={{ display:'grid', gap:'6px' }}>
                    {dClasses
                      .sort((a,b) => (a.start_time||'').localeCompare(b.start_time||''))
                      .map(c => <ClassRow key={c.id} c={c} onEditZoom={onEditZoom} onDelete={onDelete} showTeacher={showTeacher} expandable={expandable} onJoin={onJoin} gateLink={isTeacher || isStudent}/>)}
                  </div>
                </div>
              ))}

              {isStudent && view === 'upcoming' && filtered.length > PREVIEW && (
                <button onClick={() => setShowAll(v => !v)} style={{
                  width:'100%', marginTop:'14px', padding:'11px',
                  background:'rgba(30,144,255,0.08)', border:'0.5px solid rgba(30,144,255,0.2)',
                  borderRadius:'10px', color:'#5aabff', fontSize:'13px', fontWeight:700,
                  cursor:'pointer', fontFamily:'inherit',
                }}>
                  {showAll ? `↑ Show less` : `↓ View more (${filtered.length - PREVIEW} more classes)`}
                </button>
              )}
            </>
          )}
        </>
      )}

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
    </div>
  )
}
